import {
    Centered,
    ContentBlock,
    portalStoreToken,
    PortalUrls,
    usePortalStore,
} from '@servicetitan/cp-ui';
import { BodyText, Spinner } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { Loader } from '@servicetitan/web-components';
import { observer } from 'mobx-react';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { PortalAuthStore } from './modules/auth/stores/portal-auth.store';
import { useAuthHistory } from './modules/auth/utils/use-auth-history';
import { PortalLayout } from './modules/common/components/layout/portal-layout';
import { AppSettingsStore } from './modules/common/stores/app-settings.store';
import { EnvStore } from './modules/common/stores/env.store';
import { DashboardModule } from './modules/dashboard/components/dashboard.module';
import { usePortalConfig } from './utils/config';
import { getMfePortalSettings } from './utils/mfe-portal-settings';

interface MfeLoaderProps {
    name: string;
    basename: string;
    data?: Record<string, string>;
}

interface AuthorizedMfeLoaderProps extends MfeLoaderProps {
    allowAnonEstimates?: boolean;
    allowAnonPayments?: boolean;
    allowAnonServiceAgreements?: boolean;
}

const AuthorizedMfeLoader: FC<AuthorizedMfeLoaderProps> = ({
    allowAnonEstimates,
    allowAnonPayments,
    allowAnonServiceAgreements,
    ...rest
}) => {
    const [
        { authorized, isAnonymousEstimates, isAnonymousPayments, isAnonymousServiceAgreements },
    ] = useDependencies(PortalAuthStore);
    const authHistory = useAuthHistory();

    const allowAnonAccess =
        (!!allowAnonPayments && isAnonymousPayments) ||
        (!!allowAnonEstimates && isAnonymousEstimates) ||
        (!!allowAnonServiceAgreements && isAnonymousServiceAgreements);

    if (!authorized && !allowAnonAccess) {
        return <Navigate to={authHistory.path('login')} />;
    }

    return <MfeLoader {...rest} />;
};

export const MfeLoader: FC<MfeLoaderProps> = observer(({ basename, data: extraData, name }) => {
    const [{ culture, settings, user }, envStore, { authorized, brand }] = useDependencies(
        portalStoreToken,
        EnvStore,
        PortalAuthStore
    );
    let { mfeTag } = usePortalConfig();

    if (mfeTag === 'dev') {
        // Sanitation is required since package names cannot contain slashes
        mfeTag = (envStore?.settings?.environmentTag ?? 'dev').replace(/\//g, '-');
    }
    const data = useMemo(
        () => ({
            // extraData spread first so core portal keys always win
            ...extraData,
            user: JSON.stringify(user?.data),
            settings: JSON.stringify(getMfePortalSettings(settings, brand, authorized)),
            culture: JSON.stringify(culture),
        }),
        [authorized, brand, culture, settings, user, extraData]
    );

    const registryUrl = envStore?.settings?.useLocalMfeRegistry
        ? 'http://localhost:4874/@servicetitan' // Local UNPKG server
        : 'https://unpkg.servicetitan.com/@servicetitan';

    let packagePath = `${registryUrl}/${name}`;
    let fallbackPackagePath;

    if (mfeTag) {
        fallbackPackagePath = packagePath;
        packagePath = `${packagePath}@${mfeTag}`;
    }

    if (process.env.NODE_ENV === 'development') {
        const port =
            {
                'customer-portal-invoices': '9010',
                'customer-portal-estimates': '9020',
                'customer-portal-payment-methods': '9030',
                'customer-portal-memberships': '9040',
                'customer-portal-service-agreements': '9050',
                'customer-portal-jobs': '9060',
                'customer-portal-equipment': '9070',
                'customer-portal-tenant-settings': '9001',
                'customer-portal-tenant-customer': '9002',
            }[name] ?? '8090';

        packagePath = `http://foobar.localhost:${port}`;
        fallbackPackagePath = undefined;
    }

    return (
        <Loader
            key={name}
            className="w-100"
            src={packagePath}
            fallbackSrc={fallbackPackagePath}
            basename={basename}
            loadingFallback={
                <ContentBlock centered white className="w-100 h-100">
                    <Centered>
                        <Spinner size="tiny" />
                    </Centered>
                </ContentBlock>
            }
            errorFallback={<BodyText>Please refresh this page, or try again later.</BodyText>}
            data={data}
        />
    );
});

const CustomerInfoMfeLoader: FC = () => {
    const [searchParams] = useSearchParams();
    const contactOwnerId = searchParams.get('contactOwnerId') ?? '';
    const contactOwnerType = searchParams.get('contactOwnerType') ?? 'Customer';
    const data = useMemo(
        () => ({
            customerId: contactOwnerId,
            contactOwnerId,
            contactOwnerType,
        }),
        [contactOwnerId, contactOwnerType]
    );
    return (
        <MfeLoader name="customer-portal-tenant-customer" basename="/customer-info" data={data} />
    );
};

export const AuthorizedApp = observer(() => {
    const { user } = usePortalStore();
    const pickMembership = user?.pickMembership;
    const [
        { authorized, authorizedLoginless },
        {
            equipmentEnabled,
            invoiceEnabled,
            jobEnabled,
            membershipEnabled,
            proposalsEstimatesEnabled,
            serviceAgreementsEnabled,
        },
        envStore,
    ] = useDependencies(PortalAuthStore, AppSettingsStore, EnvStore);

    useEffect(() => {
        if (!envStore.settings) {
            envStore.loadData();
        }
    }, [envStore]);
    const authHistory = useAuthHistory();
    const developmentEnvironments = ['dev'];

    const membershipsRoot =
        membershipEnabled && pickMembership
            ? pickMembership(
                  PortalUrls.memberships.rootMembership,
                  PortalUrls.memberships.rootContracts
              )
            : undefined;
    return (
        <PortalLayout>
            <Routes>
                {equipmentEnabled && (
                    <Route
                        path={`${PortalUrls.equipment.root}/*`}
                        element={
                            <MfeLoader
                                name="customer-portal-equipment"
                                basename={PortalUrls.equipment.root}
                            />
                        }
                    />
                )}
                {jobEnabled && (
                    <Route
                        path={`${PortalUrls.jobs.root}/*`}
                        element={
                            <MfeLoader
                                name="customer-portal-jobs"
                                basename={PortalUrls.jobs.root}
                            />
                        }
                    />
                )}
                {(invoiceEnabled || authorizedLoginless) && (
                    <Route
                        path={`${PortalUrls.invoices.root}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-invoices"
                                basename={PortalUrls.invoices.root}
                                allowAnonPayments
                            />
                        }
                    />
                )}
                <Route
                    path={`${PortalUrls.paymentMethods.root}/*`}
                    element={
                        <AuthorizedMfeLoader
                            name="customer-portal-payment-methods"
                            basename={PortalUrls.paymentMethods.root}
                            allowAnonPayments
                        />
                    }
                />
                {(proposalsEstimatesEnabled || authorizedLoginless) && (
                    <Route
                        path={`${PortalUrls.proposals.root}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-estimates"
                                basename={PortalUrls.proposals.root}
                                allowAnonEstimates
                            />
                        }
                    />
                )}
                {!!membershipsRoot && (
                    <Route
                        path={`${membershipsRoot}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-memberships"
                                basename={membershipsRoot}
                            />
                        }
                    />
                )}
                {(serviceAgreementsEnabled || authorizedLoginless) && (
                    <Route
                        path={`${PortalUrls.serviceAgreements.root}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-service-agreements"
                                basename={PortalUrls.serviceAgreements.root}
                                allowAnonServiceAgreements
                            />
                        }
                    />
                )}

                {/*
                    This is only for testing purposes. It exposes the tenant-settings
                    and customer-info applications in order to run integration tests
                    against these endpoints. These routes should not be exposed in production.
                */}
                {developmentEnvironments.includes(envStore.settings?.environmentName ?? '') && (
                    <Fragment>
                        <Route
                            path="/tenant-settings/*"
                            element={
                                <MfeLoader
                                    name="customer-portal-tenant-settings"
                                    basename="/tenant-settings"
                                />
                            }
                        />
                        <Route path="/customer-info/*" element={<CustomerInfoMfeLoader />} />
                    </Fragment>
                )}
                <Route
                    path="/*"
                    element={
                        authorized ? (
                            <DashboardModule />
                        ) : (
                            <Navigate to={authHistory.path('login')} />
                        )
                    }
                />
            </Routes>
        </PortalLayout>
    );
});
